/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – ProfilePanel.tsx
// ============================================================

import React, { useState, useEffect } from 'react';
import { Form, Input, Switch, message, Modal } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, LockOutlined,
  BellOutlined, EditOutlined, SaveOutlined, CheckCircleOutlined,
  ClockCircleOutlined, HistoryOutlined, SafetyCertificateOutlined,
  KeyOutlined, CloseOutlined, TeamOutlined,
} from '@ant-design/icons';

import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';

// ─── Constante ────────────────────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  admin:    'Administrator',
  operator: 'Operator',
  viewer:   'Vizualizator',
};
const ROLE_COLOR: Record<string, string> = {
  admin: '#6366f1', operator: '#10b981', viewer: '#06b6d4',
};
const ROLE_BG: Record<string, string> = {
  admin: '#eef2ff', operator: '#d1fae5', viewer: '#ecfeff',
};
const ROLE_PERMS: Record<string, string[]> = {
  admin:    ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate', 'Mentenanță', 'Configurare', 'Utilizatori'],
  operator: ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate', 'Mentenanță', 'Configurare'],
  viewer:   ['General', 'Senzori', 'Alerte', 'Analiză', 'Calitate Apă', 'Predicții AI', 'Rapoarte', 'Conformitate'],
};

const SECTION_ICON: Record<string, React.ReactNode> = {
  'Auth':        <UserOutlined />,
  'Alerte':      <BellOutlined />,
  'Senzori':     <CheckCircleOutlined />,
  'Rapoarte':    <ClockCircleOutlined />,
  'Configurare': <SafetyCertificateOutlined />,
  'Utilizatori': <TeamOutlined />,
  'Mentenanță':  <EditOutlined />,
  'Profil':      <UserOutlined />,
};

// ─── Card wrapper — identic cu restul paginilor ───────────

const Card: React.FC<{
  title: string; icon: React.ReactNode;
  color?: string; children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, icon, color = '#6366f1', children, action }) => (
  <div style={{
    background: '#fff', borderRadius: 14,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: `${color}15`, fontSize: 15, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{title}</div>
      </div>
      {action}
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

// ─── Row info ─────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
    <span style={{ width: '38%', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', flex: 1 }}>{value}</span>
  </div>
);

// ─── Panel principal ──────────────────────────────────────

const ProfilePanel: React.FC = () => {
  const { user, updateUser, logActivity, getActivity } = useAuth();

  const [editMode,   setEditMode]   = useState(false);
  const [pwModal,    setPwModal]    = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPw,   setSavingPw]   = useState(false);

  const [form]   = Form.useForm();
  const [pwForm] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name:  user.name,
        email: user.email,
        phone: user.phone ?? '',
      });
    }
  }, [user, form, editMode]);

  if (!user) return null;

  const roleColor = ROLE_COLOR[user.role] ?? '#6366f1';
  const roleBg    = ROLE_BG[user.role]    ?? '#eef2ff';
  const perms     = ROLE_PERMS[user.role] ?? [];
  const activity  = getActivity(user.id).slice(0, 10);

  const saveInfo = () => {
    form.validateFields().then(vals => {
      setSavingInfo(true);
      setTimeout(() => {
        updateUser(user.id, { name: vals.name, email: vals.email, phone: vals.phone });
        logActivity('Profil actualizat', 'Profil');
        message.success('Profil actualizat cu succes!');
        setSavingInfo(false);
        setEditMode(false);
      }, 500);
    });
  };

  const savePw = () => {
    pwForm.validateFields().then(vals => {
      const users = (() => {
        try { return JSON.parse(localStorage.getItem('simadca_users_v3') ?? '[]'); } catch { return []; }
      })();
      const cu = users.find((u: any) => u.id === user.id);
      if (cu && cu.password !== vals.oldPassword) {
        message.error('Parola curentă este incorectă!');
        return;
      }
      if (vals.newPassword !== vals.confirmPassword) {
        message.error('Parolele noi nu se potrivesc!');
        return;
      }
      setSavingPw(true);
      setTimeout(() => {
        const updated = users.map((u: any) => u.id === user.id ? { ...u, password: vals.newPassword } : u);
        try { localStorage.setItem('simadca_users_v3', JSON.stringify(updated)); } catch {}
        logActivity('Parolă schimbată', 'Profil');
        message.success('Parolă schimbată cu succes!');
        setSavingPw(false);
        setPwModal(false);
        pwForm.resetFields();
      }, 600);
    });
  };

  const toggleNotif = (key: 'notifEmail' | 'notifSms' | 'notifBrowser' | 'notifDailyReport', val: boolean) => {
    updateUser(user.id, { [key]: val });
    logActivity(`Notificări ${key} ${val ? 'activate' : 'dezactivate'}`, 'Profil');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ── */}
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
          Profilul meu
        </h2>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          Setări cont · Notificări · Permisiuni · Activitate recentă
        </span>
      </div>

      {/* ── Hero utilizator ── */}
      <div style={{
        background: `linear-gradient(135deg, ${roleColor}12, ${roleColor}04)`,
        borderRadius: 16, border: `1px solid ${roleColor}20`,
        padding: '24px 28px',
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        {/* Avatar */}
        <div style={{
          width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${roleColor}, ${roleColor}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 800, color: '#fff',
          boxShadow: `0 4px 20px ${roleColor}40`,
        }}>
          {user.name.charAt(0).toUpperCase()}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            {user.name}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>{user.email}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 99, background: roleBg, color: roleColor, display: 'flex', alignItems: 'center', gap: 5 }}>
              <SafetyCertificateOutlined style={{ fontSize: 11 }} /> {ROLE_LABEL[user.role]}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 99, background: user.active ? '#d1fae5' : '#fee2e2', color: user.active ? '#059669' : '#ef4444' }}>
              {user.active ? '● Cont activ' : '● Dezactivat'}
            </span>
            {user.lastLogin && (
              <span style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClockCircleOutlined style={{ fontSize: 10 }} />
                Ultima conectare: {new Date(user.lastLogin).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        {/* Buton editare */}
        <button
          onClick={() => setEditMode(e => !e)}
          style={{
            padding: '9px 20px', borderRadius: 10, fontSize: 12, fontWeight: 700,
            border: `1px solid ${editMode ? '#e2e8f0' : roleColor}`,
            background: editMode ? '#f8fafc' : roleColor,
            color: editMode ? '#64748b' : '#fff',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
            flexShrink: 0,
          }}
        >
          {editMode ? <><CloseOutlined /> Anulează</> : <><EditOutlined /> Editează profil</>}
        </button>
      </div>

      {/* ── Rând 1: Informații cont + Parolă ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Informații cont */}
        <Card title="Informații cont" icon={<UserOutlined />} color="#6366f1">
          {editMode ? (
            /* Formular editare */
            <Form form={form} layout="vertical" size="middle">
              <Form.Item name="name" label={<span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Nume complet</span>}
                rules={[{ required: true, message: 'Câmp obligatoriu' }]} style={{ marginBottom: 12 }}>
                <Input prefix={<UserOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: 9 }} />
              </Form.Item>
              <Form.Item name="email" label={<span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Email</span>}
                rules={[{ required: true, type: 'email', message: 'Email invalid' }]} style={{ marginBottom: 12 }}>
                <Input prefix={<MailOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: 9 }} />
              </Form.Item>
              <Form.Item name="phone" label={<span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Telefon</span>}
                style={{ marginBottom: 16 }}>
                <Input prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />} style={{ borderRadius: 9 }} />
              </Form.Item>
              <button
                onClick={saveInfo} disabled={savingInfo}
                style={{ width: '100%', padding: '9px', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <SaveOutlined /> {savingInfo ? 'Se salvează…' : 'Salvează modificările'}
              </button>
            </Form>
          ) : (
            /* Mod vizualizare */
            <div>
              <InfoRow label="Nume complet" value={user.name} />
              <InfoRow label="Email"        value={user.email} />
              <InfoRow label="Telefon"      value={user.phone ?? '—'} />
              <InfoRow label="ID utilizator" value={<span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{user.id}</span>} />
              <InfoRow label="Rol"          value={
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: roleBg, color: roleColor }}>
                  {ROLE_LABEL[user.role]}
                </span>
              } />
            </div>
          )}
        </Card>

        {/* Parolă & securitate */}
        <Card title="Securitate cont" icon={<LockOutlined />} color="#f59e0b">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Parolă */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 10, border: '1px solid #f1f5f9', background: '#fafafa' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Parolă</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Ultima modificare: necunoscută</div>
              </div>
              <button
                onClick={() => setPwModal(true)}
                style={{ padding: '7px 14px', borderRadius: 9, fontSize: 11, fontWeight: 700, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <KeyOutlined /> Schimbă
              </button>
            </div>

            {/* Sesiune activă */}
            <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid #f1f5f9', background: '#fafafa' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>Sesiune activă</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Autentificat ca</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{user.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Durată sesiune</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981' }}>Activă · 8h max</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Stocare sesiune</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>localStorage</span>
                </div>
              </div>
            </div>

            {/* Status cont */}
            <div style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${user.active ? '#bbf7d0' : '#fecaca'}`, background: user.active ? '#f0fdf4' : '#fff5f5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: user.active ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: user.active ? '#065f46' : '#7f1d1d' }}>
                    {user.active ? 'Cont activ' : 'Cont dezactivat'}
                  </div>
                  <div style={{ fontSize: 11, color: user.active ? '#059669' : '#ef4444', marginTop: 1 }}>
                    {user.active ? 'Acces complet la platformă' : 'Contactați administratorul'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Rând 2: Notificări + Permisiuni ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Notificări */}
        <Card title="Preferințe notificări" icon={<BellOutlined />} color="#10b981">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'notifEmail'       as const, label: 'Notificări email',      desc: 'Alerte critice pe email', icon: <MailOutlined /> },
              { key: 'notifSms'         as const, label: 'Notificări SMS',         desc: 'SMS pentru alerte critice', icon: <PhoneOutlined /> },
              { key: 'notifBrowser'     as const, label: 'Notificări browser',     desc: 'Push notifications în browser', icon: <BellOutlined /> },
              { key: 'notifDailyReport' as const, label: 'Raport zilnic automat',  desc: 'Raport trimis zilnic la 6:00', icon: <ClockCircleOutlined /> },
            ].map(n => (
              <div key={n.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', background: user[n.key] ? '#f0fdf4' : '#fafafa', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: user[n.key] ? '#d1fae5' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: user[n.key] ? '#10b981' : '#94a3b8' }}>
                    {n.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{n.label}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{n.desc}</div>
                  </div>
                </div>
                <Switch
                  checked={user[n.key] ?? false}
                  onChange={val => toggleNotif(n.key, val)}
                  style={{ background: user[n.key] ? '#10b981' : undefined }}
                  size="small"
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Permisiuni */}
        <Card title="Permisiuni rol" icon={<SafetyCertificateOutlined />} color="#f59e0b">
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, background: roleBg, border: `1px solid ${roleColor}30` }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: roleColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff' }}>
                <SafetyCertificateOutlined />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: roleColor }}>{ROLE_LABEL[user.role]}</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{perms.length} secțiuni accesibile</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {perms.map(p => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 99, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <CheckCircleOutlined style={{ color: '#10b981', fontSize: 10 }} />
                <span style={{ fontSize: 11, color: '#065f46', fontWeight: 600 }}>{p}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6 }}>
              Rolul <strong style={{ color: roleColor }}>{ROLE_LABEL[user.role]}</strong> este alocat de administrator și nu poate fi modificat din acest panou.
            </div>
          </div>
        </Card>
      </div>

      {/* ── Rând 3: Activitate recentă full-width ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#6366f1' }}>
            <HistoryOutlined />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Activitate recentă</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Ultimele {activity.length} acțiuni înregistrate în sesiunea curentă</div>
          </div>
        </div>

        {activity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
            <HistoryOutlined style={{ fontSize: 28, display: 'block', marginBottom: 10 }} />
            <div style={{ fontSize: 13, fontWeight: 600 }}>Nicio activitate înregistrată</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Acțiunile tale vor apărea aici</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {activity.map((a, i) => (
              <div key={a.id} style={{
                display: 'flex', gap: 12, padding: '12px 20px', alignItems: 'center',
                borderBottom: i < activity.length - 1 ? '1px solid #f8fafc' : 'none',
                borderRight: i % 2 === 0 ? '1px solid #f8fafc' : 'none',
                transition: 'background 0.1s',
              }}
                onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
                onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#6366f1', flexShrink: 0 }}>
                  {SECTION_ICON[a.section] ?? <ClockCircleOutlined />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.action}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 8 }}>
                    <span style={{ padding: '1px 6px', borderRadius: 99, background: '#f1f5f9', color: '#64748b', fontSize: 9, fontWeight: 600 }}>
                      {a.section}
                    </span>
                    <span>
                      {new Date(a.timestamp).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modal schimbare parolă ── */}
      <Modal
        open={pwModal}
        onCancel={() => { setPwModal(false); pwForm.resetFields(); }}
        onOk={savePw}
        okText={savingPw ? 'Se schimbă…' : 'Schimbă parola'}
        cancelText="Anulează"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#d97706' }}>
              <KeyOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Schimbare parolă</div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{user.email}</div>
            </div>
          </div>
        }
        width={440}
        okButtonProps={{ disabled: savingPw, style: { background: '#6366f1', borderColor: '#6366f1' } }}
      >
        <Form form={pwForm} layout="vertical" style={{ marginTop: 20 }} requiredMark={false}>
          <Form.Item
            name="oldPassword" label={<span style={{ fontSize: 12, fontWeight: 600 }}>Parola curentă</span>}
            rules={[{ required: true, message: 'Introduceți parola curentă' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Parola curentă…"
              style={{ borderRadius: 9 }}
            />
          </Form.Item>
          <Form.Item
            name="newPassword" label={<span style={{ fontSize: 12, fontWeight: 600 }}>Parola nouă</span>}
            rules={[{ required: true }, { min: 6, message: 'Minim 6 caractere' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Minim 6 caractere"
              style={{ borderRadius: 9 }}
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword" label={<span style={{ fontSize: 12, fontWeight: 600 }}>Confirmă parola nouă</span>}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirmați parola' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Parolele nu se potrivesc!'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Confirmă parola nouă"
              style={{ borderRadius: 9 }}
            />
          </Form.Item>
        </Form>
        <div style={{ fontSize: 11, color: '#94a3b8', padding: '10px 14px', borderRadius: 9, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
          Parola curentă trebuie să fie corectă. Noua parolă trebuie să aibă minim 6 caractere.
        </div>
      </Modal>

    </div>
  );
};

export default ProfilePanel;